import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form } from "react-bootstrap";
import {
  fetchCourses,
  deleteCourse,
  clearError,
  clearSuccess,
} from "../../redux/slices/courseSlice";
import { fetchUniversities } from "../../redux/slices/universitySlice";
import CourseModal from "./CourseModal";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import DynamicBadge from "../Common/DynamicBadge";
import { FaPencilAlt, FaRegTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const DATATABLE_PAGINATION_OPTIONS = {
  initialState: { rowsPerPage: 10, options: [5, 10, 20, 50] }
};

const SafeImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  return hasError || !src ? (
    <div
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "4px",
        backgroundColor: "#f8f9fa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid #dee2e6"
      }}
    >
      <i className="bi bi-journal-bookmark text-secondary" style={{ fontSize: "1.2rem" }}></i>
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      style={{
        width: "40px",
        height: "40px",
        objectFit: "cover",
        borderRadius: "4px",
        border: "1px solid #dee2e6"
      }}
      onError={() => setHasError(true)}
    />
  );
};

const Courses = () => {
  const dispatch = useDispatch();
  const { courses = [], isLoading, error, success } = useSelector((state) => state.courses);
  const { universities = [] } = useSelector((state) => state.universities);

  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [universityFilter, setUniversityFilter] = useState("");

  const [isRefresh, setIsRefresh] = useState(false);

  useEffect(() => {
    dispatch(fetchCourses());
    dispatch(fetchUniversities());
  }, [dispatch, isRefresh]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  const handleEdit = (course) => {
    setEditingCourse(course);
    setShowModal(true);
  };

  const handleDeleteClick = (course) => {
    setCourseToDelete(course);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (courseToDelete) {
      dispatch(deleteCourse(courseToDelete.id));
      setShowConfirmModal(false);
      setCourseToDelete(null);
      setIsRefresh((prev) => !prev);
    }
  };

  const filteredCourses = universityFilter
    ? courses.filter((c) => c.university_id === universityFilter)
    : courses;

  const header = [
    {
      prop: "course_name",
      title: "Course Name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (
        <span className="fw-bold text-primary">{row.course_name}</span>
      )
    },
    {
      prop: "image_url",
      title: "Image",
      cell: (row) => (
        <SafeImage src={row.image_url} alt={row.course_name} />
      ),
    },
    {
      prop: "university_name",
      title: "University",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <DynamicBadge text={row.university_name || "Unknown"} />
      ),
    },
    {
      prop: "createddate",
      title: "Created Date",
      isSortable: true,
      isFilterable: true,
      cell: (row) => formatDateToDDMMYYYY(row.createddate) || "-",
    },
    {
      prop: "actions",
      title: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button variant="outline-dark" size="sm" onClick={() => handleEdit(row)}>
            <FaPencilAlt />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteClick(row)}>
            <FaRegTrashAlt />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="text-center mt-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Link className="breadcrum" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Courses
        </Link>
      </div>

      <DatatableWrapper
        body={filteredCourses}
        headers={header}
        paginationOptionsProps={DATATABLE_PAGINATION_OPTIONS}
      >
        <div className="compact-controls d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ maxWidth: "250px" }}>
              <Filter placeholder="Search courses..." className="w-100" />
            </div>
            <div style={{ maxWidth: "200px" }}>
              <Form.Select
                value={universityFilter}
                onChange={(e) => setUniversityFilter(e.target.value)}
                className="btn-sm"
              >
                <option value="">All Universities</option>
                {universities.map((uni) => (
                  <option key={uni.id} value={uni.id}>
                    {uni.university_name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <PaginationOptions />
            <InfoPill
              left="Total Courses"
              right={filteredCourses?.length}
            />
          </div>

          <div className="d-flex align-items-center">
            <Button
              variant="dark"
              className="btn-sm"
              onClick={() => {
                setEditingCourse(null);
                setShowModal(true);
              }}
            >
              New Course
            </Button>
          </div>
        </div>

        <div className="table-responsive shadow-sm">
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      <CourseModal show={showModal} onHide={() => {
        setShowModal(false);
        setIsRefresh((prev) => !prev);
      }} course={editingCourse} />

      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Course"
        message={`Are you sure you want to delete the course "${courseToDelete?.course_name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isLoading}
      />
    </div>
  );
};

export default Courses;
